import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown } from "react-icons/fa";
import { countryList } from '../../utils/countryData';
import { stldigitalCountryList } from '../../utils/stldigitalCountryData';
import { getRegionsForCountry } from '../../utils/regionData';
import { getStlDigitalRegionsForCountry } from '../../utils/stldigitalRegionData';

const Step1_Organization = ({
    vendorData,
    handleVendorChange,
    isDisabled,
    vendorOnboardInfo
}) => {

    // Dropdown States specific to this step
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const countryDropdownRef = useRef(null);

    const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
    const [regionSearchTerm, setRegionSearchTerm] = useState('');
    const regionDropdownRef = useRef(null);

    // Helpers to determine STL-DIGITAL context
    const isStlDigital = vendorOnboardInfo?.company &&
        (vendorOnboardInfo.company.trim().replace('-', '').toLowerCase() === 'stldigital' ||
            vendorOnboardInfo.company.trim().toLowerCase() === 'stl-digital');

    // Click outside handler for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setIsCountryDropdownOpen(false);
            }
            if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
                setIsRegionDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="vendor-card">
            <h3>{isStlDigital ? 'Vendor Organization Detail' : 'Personal Information'}</h3>
            <div className="two-col-grid">

                {/* VENDOR NAME (FROZEN) */}
                <div>
                    <span className="input-label">Vendor Name</span>
                    <input
                        type="text"
                        name="vendorName"
                        value={vendorOnboardInfo?.vendor_name || ''}
                        disabled={true}
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    />
                </div>

                {/* COMPANY NAME (FROZEN) */}
                <div>
                    <span className="input-label">Company Name</span>
                    <input
                        type="text"
                        name="companyName"
                        value={vendorOnboardInfo?.company || ''}
                        disabled={true}
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    />
                </div>

                {/* FIRST NAME / COMPANY NAME */}
                <div>
                    <span className="input-label">{isStlDigital ? 'Company Name*' : 'First Name*'}</span>
                    <input
                        type="text"
                        name="firstName"
                        placeholder={isStlDigital ? 'Company Name*' : 'First Name*'}
                        value={vendorData.firstName}
                        onChange={handleVendorChange}
                        disabled={isDisabled}
                    />
                </div>

                {/* LAST NAME / COMPANY NAME EXT */}
                <div>
                    <span className="input-label">{isStlDigital ? 'Company Name Ext (Optional)' : 'Last Name*'}</span>
                    <input
                        type="text"
                        name="lastName"
                        placeholder={isStlDigital ? 'Company Name Ext (Optional)' : 'Last Name*'}
                        value={vendorData.lastName}
                        onChange={handleVendorChange}
                        disabled={isDisabled}
                    />
                </div>

                {/* CONTACT NAME */}
                <div>
                    <span className="input-label">Contact Person Name</span>
                    <input
                        type="text"
                        name="contactName"
                        placeholder="Contact Person Name"
                        value={vendorData.contactName}
                        onChange={handleVendorChange}
                        disabled={isDisabled}
                    />
                </div>

                {/* EMAIL */}
                <div>
                    <span className="input-label">Email*</span>
                    <input
                        type="email"
                        name="email"
                        placeholder="Email*"
                        value={vendorData.email}
                        onChange={handleVendorChange}
                        disabled={isDisabled}
                    />
                </div>

                {/* PHONE */}
                <div>
                    <span className="input-label">Phone Number*</span>
                    <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number*"
                        value={vendorData.phone}
                        onChange={(e) => {
                            // allow only digits
                            const cleaned = e.target.value.replace(/\D/g, '');
                            // limit to 10 digits max
                            if (cleaned.length <= 10) {
                                handleVendorChange({ target: { name: 'phone', value: cleaned } });
                            }
                        }}
                        maxLength="10"
                        disabled={isDisabled}
                    />
                </div>

                {/* COUNTRY DROPDOWN */}
                <div>
                    <span className="input-label">Country*</span>
                    <div ref={countryDropdownRef}>
                        <div style={{ position: 'relative', marginTop: '12px' }}>
                            <div
                                onClick={() => {
                                    if (!isDisabled) {
                                        setIsCountryDropdownOpen(!isCountryDropdownOpen);
                                        if (!isCountryDropdownOpen) setCountrySearchTerm('');
                                    }
                                }}
                                style={{
                                    padding: '10px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '6px',
                                    width: '100%',
                                    backgroundColor: isDisabled ? '#f3f4f6' : '#fff',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    minHeight: '45px'
                                }}
                            >
                                <span style={{ color: vendorData.country ? '#000' : '#888' }}>
                                    {vendorData.country ? vendorData.country : "Select Country"}
                                </span>
                                <FaChevronDown style={{ color: 'gray', fontSize: '12px' }} />
                            </div>

                            {isCountryDropdownOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    width: '100%',
                                    maxHeight: '250px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    zIndex: 1000,
                                    marginTop: '4px',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        <input
                                            type="text"
                                            placeholder="Search country..."
                                            value={countrySearchTerm}
                                            onChange={(e) => setCountrySearchTerm(e.target.value)}
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="dropdown-scrollbar" style={{ overflowY: 'auto', maxHeight: '200px' }}>
                                        {(isStlDigital ? stldigitalCountryList : countryList)
                                            .filter(c =>
                                                c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
                                                c.code.toLowerCase().includes(countrySearchTerm.toLowerCase())
                                            )
                                            .map((country) => (
                                                <div
                                                    key={country.code}
                                                    onClick={() => {
                                                        handleVendorChange({ target: { name: 'country', value: country.code } });
                                                        setIsCountryDropdownOpen(false);
                                                    }}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid #f7f7f7',
                                                        backgroundColor: vendorData.country === country.code ? '#eef2ff' : 'transparent',
                                                        color: vendorData.country === country.code ? '#4f46e5' : '#333'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = vendorData.country === country.code ? '#eef2ff' : 'transparent'}
                                                >
                                                    <span style={{ fontWeight: 500, marginRight: '8px' }}>{country.code}</span>
                                                    {country.name}
                                                </div>
                                            ))}
                                        {(isStlDigital ? stldigitalCountryList : countryList)
                                            .filter(c => c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) || c.code.toLowerCase().includes(countrySearchTerm.toLowerCase())).length === 0 && (
                                                <div style={{ padding: '12px', color: '#888', textAlign: 'center' }}>
                                                    No countries found
                                                </div>
                                            )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* CITY */}
                <div>
                    <span className="input-label">City*</span>
                    <input
                        type="text"
                        name="city"
                        placeholder="City"
                        value={vendorData.city}
                        onChange={handleVendorChange}
                        disabled={isDisabled}
                    />
                </div>

                {/* REGION DROPDOWN */}
                <div ref={regionDropdownRef}>
                    <span className="input-label">Region*</span>
                    <div style={{ position: 'relative', marginTop: '12px' }}>
                        <div
                            onClick={() => {
                                if (!isDisabled && vendorData.country) {
                                    setIsRegionDropdownOpen(!isRegionDropdownOpen);
                                    if (!isRegionDropdownOpen) setRegionSearchTerm('');
                                }
                            }}
                            style={{
                                padding: '10px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                width: '100%',
                                backgroundColor: isDisabled || !vendorData.country ? '#f3f4f6' : '#fff',
                                cursor: isDisabled || !vendorData.country ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                minHeight: '45px'
                            }}
                        >
                            <span style={{ color: vendorData.region ? '#000' : '#888' }}>
                                {vendorData.region || "Select Region"}
                            </span>
                            <FaChevronDown style={{ color: 'gray', fontSize: '12px' }} />
                        </div>

                        {isRegionDropdownOpen && vendorData.country && (() => {
                            const availableRegions = isStlDigital
                                ? getStlDigitalRegionsForCountry(vendorData.country)
                                : getRegionsForCountry(vendorData.country);
                            const filteredRegions = availableRegions.filter(r =>
                                r.region.toLowerCase().includes(regionSearchTerm.toLowerCase()) ||
                                r.description.toLowerCase().includes(regionSearchTerm.toLowerCase())
                            );

                            return (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    width: '100%',
                                    maxHeight: '160px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    zIndex: 1000,
                                    marginTop: '4px',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                        <input
                                            type="text"
                                            placeholder="Search region..."
                                            value={regionSearchTerm}
                                            onChange={(e) => setRegionSearchTerm(e.target.value)}
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="dropdown-scrollbar" style={{ overflowY: 'auto', maxHeight: '150px' }}>
                                        {filteredRegions.map((region) => (
                                            <div
                                                key={`${region.countryCode}-${region.region}`}
                                                onClick={() => {
                                                    handleVendorChange({ target: { name: 'region', value: region.region } });
                                                    setIsRegionDropdownOpen(false);
                                                }}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid #f7f7f7',
                                                    backgroundColor: vendorData.region === region.region ? '#eef2ff' : 'transparent',
                                                    color: vendorData.region === region.region ? '#4f46e5' : '#333'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = vendorData.region === region.region ? '#eef2ff' : 'transparent'}
                                            >
                                                <span style={{ fontWeight: 500, marginRight: '8px' }}>{region.region}</span>
                                                {region.description}
                                            </div>
                                        ))}
                                        {filteredRegions.length === 0 && (
                                            <div style={{ padding: '12px', color: '#888', textAlign: 'center' }}>
                                                No regions found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* STL-DIGITAL SPECIFIC FIELDS */}
                {isStlDigital && (
                    <>
                        <div>
                            <span className="input-label">District</span>
                            <input
                                type="text"
                                name="district"
                                placeholder="District"
                                value={vendorData.district}
                                onChange={handleVendorChange}
                                disabled={isDisabled}
                            />
                        </div>
                        <div>
                            <span className="input-label">House Number</span>
                            <input
                                type="text"
                                name="house_number"
                                placeholder="House Number"
                                value={vendorData.house_number}
                                onChange={handleVendorChange}
                                disabled={isDisabled}
                            />
                        </div>
                    </>
                )}

                {/* POSTAL CODE */}
                <div>
                    <span className="input-label">Postal Code*</span>
                    <input
                        type="text"
                        name="postal_code"
                        placeholder="Zip/Pin Code"
                        value={vendorData.postal_code}
                        onChange={handleVendorChange}
                        disabled={isDisabled}
                    />
                </div>

                {/* ADDRESS */}
                <div>
                    <span className="input-label">Registered Office Address*</span>
                    <textarea
                        name="address"
                        placeholder="Registered Office Address*"
                        value={vendorData.address}
                        onChange={handleVendorChange}
                        disabled={isDisabled}
                    />
                </div>

                {/* REGISTERED OFFICE ADDRESS EXT (STL-DIGITAL) */}
                {isStlDigital && (vendorData.address?.length >= 60 || vendorData.registered_office_address_ext) && (
                    <div>
                        <span className="input-label">Registered Office Address Ext*</span>
                        <input
                            type="text"
                            name="registered_office_address_ext"
                            placeholder="Registered Office Address Ext*"
                            value={vendorData.registered_office_address_ext}
                            onChange={(e) => {
                                if (e.target.value.length <= 40) {
                                    handleVendorChange(e);
                                }
                            }}
                            disabled={isDisabled}
                        />
                    </div>
                )}

            </div>
        </div>
    );
};

export default Step1_Organization;
